import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Trophy, Clock, Target, UserX, ArrowLeft, MessageSquare } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MatchDetailView() {
  const { partidoId } = useParams();
  const navigate = useNavigate();
  const [partido, setPartido] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (partidoId) {
      loadPartido();
      // Auto-refresh every 5 seconds
      const interval = setInterval(loadPartido, 5000);
      return () => clearInterval(interval);
    }
  }, [partidoId]);

  const loadPartido = async () => {
    try {
      const response = await axios.get(`${API}/public/partido/${partidoId}`);
      setPartido(response.data);
    } catch (error) {
      toast.error('Error al cargar el partido');
    } finally {
      setLoading(false);
    }
  };

  const getPhaseColor = (fase) => {
    if (fase === 'Resultado Final') return 'bg-slate-600';
    if (fase === '1er Tiempo' || fase === '2do Tiempo') return 'bg-green-600 animate-pulse';
    if (fase === 'Entretiempo') return 'bg-orange-500';
    if (fase === 'Por Comenzar') return 'bg-blue-500';
    if (fase === 'Atrasado') return 'bg-red-600 animate-pulse';
    return 'bg-slate-400';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-200">Cargando partido...</p>
        </div>
      </div>
    );
  }

  if (!partido) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-900 to-slate-800">
        <Card className="p-8 text-center bg-slate-800 border-slate-700">
          <p className="text-slate-200">Partido no encontrado</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 text-white">
      {/* Header with Back Button */}
      <div className="container mx-auto px-4 py-4">
        <Button
          onClick={() => navigate(-1)}
          variant="ghost"
          className="gap-2 text-white hover:bg-slate-700"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver
        </Button>
      </div>

      {/* Main Scoreboard */}
      <div className="container mx-auto px-4 py-4">
        {/* Header with Time and Estado Badge */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 text-slate-300">
            <Clock className="w-5 h-5" />
            <span className="text-xl font-semibold">{partido.hora}</span>
          </div>
          
          {/* Badge de Estado */}
          <span className={`text-sm font-semibold px-4 py-2 rounded-full ${
            partido.estado === 'finalizado' 
              ? 'bg-slate-600 text-white' 
              : partido.estado === 'en_progreso' 
                ? 'bg-green-500 text-white' 
                : 'bg-blue-500 text-white'
          }`}>
            {partido.estado === 'finalizado' ? 'Finalizado' : partido.estado === 'en_progreso' ? 'Vivo' : 'Pendiente'}
          </span>
        </div>

        {/* Phase Badge - Centro */}
        {partido.fase && (
          <div className="flex justify-center mb-6">
            <div className={`${getPhaseColor(partido.fase)} px-6 py-2 rounded-full text-white font-bold text-lg shadow-lg`}>
              {partido.fase}
            </div>
          </div>
        )}

        {/* Score Display */}
        <Card className="bg-slate-800 border-slate-700 p-4 sm:p-8 mb-6 shadow-2xl">
          <div className="grid grid-cols-3 items-center gap-2 sm:gap-4">
            {/* Local Team */}
            <div className="text-center">
              {partido.rival1_info?.logo && (
                <img 
                  src={partido.rival1_info.logo} 
                  alt={partido.rival1_nombre_corto}
                  className="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded-full mx-auto mb-2 sm:mb-3 border-4 border-emerald-500 shadow-lg"
                />
              )}
              <h2 className="text-sm sm:text-xl font-bold text-white mb-1 sm:mb-2">
                {partido.rival1_nombre_corto}
              </h2>
              <div className="text-4xl sm:text-6xl font-black text-emerald-400">
                {partido.resultado?.goles_local || 0}
              </div>
              {partido.resultado?.expulsiones_local > 0 && (
                <div className="flex items-center justify-center gap-1 mt-2 text-red-500">
                  <UserX className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="font-semibold text-sm sm:text-base">{partido.resultado.expulsiones_local}</span>
                </div>
              )}
            </div>

            {/* VS Divider */}
            <div className="text-center">
              <div className="text-xl sm:text-3xl font-bold text-slate-500">VS</div>
            </div>

            {/* Visiting Team */}
            <div className="text-center">
              {partido.rival2_info?.logo && (
                <img 
                  src={partido.rival2_info.logo} 
                  alt={partido.rival2_nombre_corto}
                  className="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded-full mx-auto mb-2 sm:mb-3 border-4 border-emerald-500 shadow-lg"
                />
              )}
              <h2 className="text-sm sm:text-xl font-bold text-white mb-1 sm:mb-2">
                {partido.rival2_nombre_corto}
              </h2>
              <div className="text-4xl sm:text-6xl font-black text-emerald-400">
                {partido.resultado?.goles_visita || 0}
              </div>
              {partido.resultado?.expulsiones_visita > 0 && (
                <div className="flex items-center justify-center gap-1 mt-2 text-red-500">
                  <UserX className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="font-semibold text-sm sm:text-base">{partido.resultado.expulsiones_visita}</span>
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* Events Timeline */}
        {partido.eventos && partido.eventos.length > 0 && (
          <Card className="bg-slate-800 border-slate-700 p-6 mb-6 shadow-xl">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Trophy className="w-5 h-5 text-emerald-500" />
              Eventos del Partido
            </h3>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {partido.eventos.map((evento, index) => (
                <div
                  key={evento.id || index}
                  className="flex items-start gap-3 p-3 bg-slate-700/50 rounded-lg border border-slate-600"
                >
                  <div className="flex-shrink-0 mt-1">
                    {evento.tipo === 'gol' && (
                      <Target className="w-5 h-5 text-emerald-500" />
                    )}
                    {evento.tipo === 'expulsion' && (
                      <UserX className="w-5 h-5 text-red-500" />
                    )}
                    {!['gol', 'expulsion'].includes(evento.tipo) && (
                      <Clock className="w-5 h-5 text-slate-400" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-white">{evento.descripcion}</p>
                    {evento.minuto != null && (
                      <p className="text-sm text-emerald-400">Minuto {evento.minuto}'</p>
                    )}
                    <p className="text-xs text-slate-400">
                      {new Date(evento.timestamp).toLocaleTimeString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Final Comment */}
        {partido.resultado?.comentario_final && (
          <Card className="bg-emerald-900/30 border-emerald-700 p-6 shadow-xl">
            <h3 className="text-lg font-bold text-emerald-400 mb-3 flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Comentario Final
            </h3>
            <p className="text-white">{partido.resultado.comentario_final}</p>
          </Card>
        )}
      </div>

      {/* Auto-refresh indicator */}
      <div className="fixed bottom-4 right-4 bg-slate-700/80 px-3 py-2 rounded-full text-xs text-slate-300 shadow-lg">
        Actualización automática cada 5s
      </div>
    </div>
  );
}
